// src/context/CurrencyContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Decides whether the visitor sees £ GBP or $ USD.
//
// 1. If the visitor picked a currency before, use that (saved in localStorage).
// 2. Otherwise auto-detect from the browser's time zone: anyone in the Americas
//    gets USD, everyone else gets GBP. No IP lookup, no API, works instantly.
// 3. If US sales are switched off in the admin, everyone sees GBP.
//
// Wrap the app in <CurrencyProvider> (outside <CartProvider>), then use
// `const { currency, formatPrice } = useCurrency();` anywhere.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
    normaliseSettings,
    isUsStoreReady,
    formatPrice as formatPriceIn,
    getVariantPrice,
    getFromPrice,
} from '../utils/pricing';

const STORAGE_KEY = 'bm_currency';

const CurrencyContext = createContext(null);

const readSavedCurrency = () => {
    try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        return saved === 'GBP' || saved === 'USD' ? saved : null;
    } catch {
        return null; // private browsing / storage blocked
    }
};

const saveCurrency = (currency) => {
    try { window.localStorage.setItem(STORAGE_KEY, currency); } catch { /* ignore */ }
};

export const detectCurrency = () => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (tz.startsWith('America/') || tz === 'Pacific/Honolulu' || tz.startsWith('US/')) return 'USD';
    } catch { /* fall through */ }
    return 'GBP';
};

export const CurrencyProvider = ({ children }) => {
    const [settings, setSettings] = useState(normaliseSettings());
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [chosenCurrency, setChosenCurrency] = useState(() => readSavedCurrency() || detectCurrency());

    useEffect(() => {
        let cancelled = false;
        getDoc(doc(db, 'settings', 'pricing'))
            .then(snap => { if (!cancelled) setSettings(normaliseSettings(snap.exists() ? snap.data() : {})); })
            .catch(err => console.error('Could not load pricing settings:', err))
            .finally(() => { if (!cancelled) setSettingsLoaded(true); });
        return () => { cancelled = true; };
    }, []);

    const usAvailable = isUsStoreReady(settings);

    // US sales off → everyone sees GBP, whatever they picked
    const currency = usAvailable ? chosenCurrency : 'GBP';

    const setCurrency = useCallback((next) => {
        if (next !== 'GBP' && next !== 'USD') return;
        setChosenCurrency(next);
        saveCurrency(next);
    }, []);

    const value = useMemo(() => ({
        currency,
        setCurrency,
        usAvailable,
        settings,
        settingsLoaded,
        symbol: currency === 'USD' ? '$' : '£',
        shipsTo: currency === 'USD' ? 'United States' : 'United Kingdom',
        formatPrice: (amount) => formatPriceIn(amount, currency),
        priceFor: (product, variantKey) => getVariantPrice(product, variantKey, currency),
        fromPrice: (product) => getFromPrice(product, currency),
    }), [currency, setCurrency, usAvailable, settings, settingsLoaded]);

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
    const ctx = useContext(CurrencyContext);
    if (!ctx) throw new Error('useCurrency must be used inside <CurrencyProvider>');
    return ctx;
};